import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { Sidebar } from 'primeng/primeng';
import { SelectItem } from 'primeng/components/common/selectitem';
import { StrtInitiative } from '../../../../../shared/models/plan/StrtInitiative';
import { TagService } from '../../../../services/tag.service';
import { StepComponent } from '../../../step.component';
import { BehaviorSubject, Observable, Subscription } from 'rxjs/index';
import { StrtKeyMeasure, StrtKeyMeasurePending } from '../../../../../shared/models/plan/StrtKeyMeasure';
import { KeyMeasureService } from '../../../../services/key-measure.service';
import { StrtPriority } from '../../../../../shared/models/plan/StrtPriority';
import { ITag } from '../../../../services/ITag';
import { StrtObjective } from '../../../../../shared/models/plan/StrtObjective';
import * as _ from 'lodash';
import {DataUtils} from '../../../../../shared/utilities/data-utils';


@Component({
  selector: 'app-key-measure',
  templateUrl: './key-measure.component.html',
  styleUrls: ['./key-measure.component.scss']
})
export class KeyMeasureComponent implements OnInit, OnDestroy, OnChanges {
  readonly MAX_MEASURE_LENGTH = 50;
  readonly MAX_KEY_MEASURES = 10;

  @Output() create = new EventEmitter<StrtKeyMeasure[]>();
  @Output() createError = new EventEmitter<Error>();
  @Output() edit = new EventEmitter<StrtKeyMeasure>();
  @Output() editError = new EventEmitter<Error>();
  @Output() cancel = new EventEmitter<void>();

  @Input() strategyId: number;
  @Input() hasKeyMeasures: boolean;

  private _isActive = false;
  private _isEdit = false;
  private _keyMeasureForm: FormGroup;
  private _pristineInitiative: StrtInitiative;
  private _pristineKeyMeasure: StrtKeyMeasure;
  private _tagSubscription: Subscription;
  private _validationTimeout: any;
  uniqueValidated = false;

  keyMeasures: StrtKeyMeasurePending[] = [];
  tagSelectItems: SelectItem[] = [];
  keyMeasureSelectItems: SelectItem[] = [];
  private _keyMeasureCreateMethodSubscription: Subscription;
  private _createMethod = 'select';
  private _rawTags: ITag[];
  private _priorities: StrtPriority[];
  private _formLoadingSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  formLoading: Observable<boolean> = this._formLoadingSubject.asObservable();

  constructor(private formBuilder: FormBuilder,
              private tagService: TagService,
              private _keyMeasureService: KeyMeasureService) { }

  ngOnInit() {
    this._formLoadingSubject.next(true);
    this._createMethod = 'select';
    this.createForm();
    this._tagSubscription = this.tagService.getSQFTags().subscribe(it => {
      this._rawTags = it;
      this.tagSelectItems.push(...this.tagService.getTagSelectItems(it));
    });
  }

  ngOnDestroy(): void {
    if (this._tagSubscription) {
      this._tagSubscription.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    StepComponent.logNgOnChanges(changes);
  }

  get headerContent() {
    return this.isEdit ? 'Edit Key Measure' : 'Add Key Measure(s)';
  }

  get initiativeDescription() {
    return this._pristineInitiative ? this._pristineInitiative.description : '';
  }

  get keyMeasureForm() {
    return this._keyMeasureForm;
  }

  get measure() {
    return this.keyMeasureForm.get('measure');
  }

  get tag() {
    return this.keyMeasureForm.get('tag');
  }

  get existingKeyMeasure() {
    return this.keyMeasureForm.get('existingKeyMeasure');
  }

  get keyMeasureCreateMethod() {
    return this.keyMeasureForm.get('keyMeasureCreateMethod');
  }

  get parentId(): number {
    return this._pristineInitiative ? this._pristineInitiative.initiativeId : null;
  }

  get isEdit() {
    return this._isEdit;
  }

  get isAddDisabled(): boolean {
    const pristineKeyMeasureCount = (this._pristineInitiative && this._pristineInitiative.keyMeasures || []).length;
    if (this.hasKeyMeasures && this._createMethod === 'select') {
      return !this.existingKeyMeasure.value || this.existingKeyMeasure.value === 0
        || (pristineKeyMeasureCount + this.keyMeasures.length) >= this.MAX_KEY_MEASURES;
    } else {
      if (!this.measure || !this.tag || this.measure.hasError('duplicateKeyMeasure')) {
        return true;
      }
      return (!this.measure.value.length || !this.tag.value)
        || (pristineKeyMeasureCount + this.keyMeasures.length) >= this.MAX_KEY_MEASURES;
    }
  }

  get isSubmitDisabled() {
    if (this.isEdit) {
      return this.keyMeasureForm.status !== 'VALID' || this.keyMeasureForm.pristine || !this.keyMeasureForm.dirty || !this.uniqueValidated;
    } else {
      return this.keyMeasures.length === 0;
    }
  }

  /**
   * Does not get called when in select mode
   * @returns {number}
   */
  get measureRemainingCharacters() {
    return this.MAX_MEASURE_LENGTH - this.measure.value.length;
  }

  get isActive() {
    return this._isActive;
  }

  set isActive(isActive) {
    this._isActive = isActive;
  }

  createKeyMeasures(initiative: StrtInitiative, priorities: StrtPriority[]) {
    this._formLoadingSubject.next(true);
    this._isActive = true;
    this._isEdit = false;
    this._createMethod = 'select';
    this._priorities = priorities;
    this._pristineInitiative = initiative;
    this.setSelectItems();
    this.resetForm();
    this.createForm();
    this._pristineInitiative = initiative;
  }

  setSelectItems() {
    this.keyMeasureSelectItems = _.uniqBy(DataUtils.flatten(this._priorities.map((priority: StrtPriority) => {
      return priority.objectives.map((objective: StrtObjective) => {
        return objective.initiatives.map((initiativeX: StrtInitiative) => {
          return initiativeX.keyMeasures.filter((reduceKeyMeasure: StrtKeyMeasure) => {
            return !this._pristineInitiative.keyMeasures.find((matchKeyMeasure: StrtKeyMeasure) => {
              return reduceKeyMeasure.keyMeasureId === matchKeyMeasure.keyMeasureId;
            }) && !this.keyMeasures.find((pendingKeyMeasure: StrtKeyMeasurePending) => {
              return reduceKeyMeasure.keyMeasureId === pendingKeyMeasure.keyMeasureId;
            });
          }).map((keyMeasure: StrtKeyMeasure) => {
            return this.keyMeasureToSelectItem(keyMeasure);
          });
        });
      });
    })), (item: SelectItem) => {
      return item.value.keyMeasureId;
    });
  }

  editKeyMeasure(initiative: StrtInitiative, keyMeasure: StrtKeyMeasure) {
    this._formLoadingSubject.next(true);
    this._isActive = true;
    this._isEdit = true;
    this.resetForm();
    this.createForm();
    this._pristineInitiative = initiative;
    this._pristineKeyMeasure = keyMeasure;
    this.keyMeasureForm.patchValue({measure: this._pristineKeyMeasure.measure, tag: this._pristineKeyMeasure.tagId});
  }

  onShowSidebar(sidebar: Sidebar) {
    sidebar.maskClickListener();
  }

  onHideSidebar() {
    this._isActive = false;
    this._isEdit = false;
    this.resetForm();
  }

  onSubmit() {
    const keyMeasures = this.formToKeyMeasure();
    if (this.isEdit) {
      this.edit.emit(keyMeasures as StrtKeyMeasurePending);
    } else {
      this.create.emit(keyMeasures as StrtKeyMeasurePending[]);
    }
  }

  onAddMeasureClick(): void {
    let keyMeasure: StrtKeyMeasurePending;
    if (this.hasKeyMeasures && this._createMethod === 'select') {
      keyMeasure = {
        initiativeId: this._pristineInitiative.initiativeId,
        keyMeasureId: this.existingKeyMeasure.value.keyMeasureId,
        measure: this.existingKeyMeasure.value.measure,
        tagId: this.existingKeyMeasure.value.tagId,
        createMethod: 'select',
        reused: true
      };
    } else {
      keyMeasure = {
        initiativeId: this._pristineInitiative.initiativeId,
        keyMeasureId: undefined,
        measure: this.measure.value,
        tagId: this.tag.value,
        createMethod: 'create',
        reused: false
      };
    }

    this.keyMeasures = [...this.keyMeasures, keyMeasure];

    if (this._createMethod === 'select') {
      // Update dropdown available options
      this.setSelectItems();
    }
    this.clearForm();
  }

  onDeleteMeasureClick(keyMeasure: StrtKeyMeasure): void {
    this.keyMeasures = this.keyMeasures.filter(it => it !== keyMeasure);
    this.setSelectItems();
  }

  private createForm() {
    this._formLoadingSubject.next(true);
    if (this.isEdit) {
      this._keyMeasureForm = this.formBuilder.group({
        measure: ['', [Validators.maxLength(this.MAX_MEASURE_LENGTH), Validators.required]],
        tag: ['', [Validators.required]]
      });
    } else {
      if (this.hasKeyMeasures) {
        const createMethod = this._createMethod ? this._createMethod : 'select';
        if (createMethod === 'select') {
          this._keyMeasureForm = this.formBuilder.group({
            keyMeasureCreateMethod: ['select'],
            existingKeyMeasure: ['', [Validators.required]]
          });
        } else if (createMethod === 'create') {
          this._keyMeasureForm = this.formBuilder.group({
            keyMeasureCreateMethod: ['create'],
            measure: ['', [Validators.maxLength(this.MAX_MEASURE_LENGTH), Validators.required]],
            tag: ['', [Validators.required]]
          });
        }
      } else {
        this._keyMeasureForm = this.formBuilder.group({
          measure: ['', [Validators.maxLength(this.MAX_MEASURE_LENGTH), Validators.required]],
          tag: ['', [Validators.required]]
        });
      }
      if (this._keyMeasureCreateMethodSubscription) {
        this._keyMeasureCreateMethodSubscription.unsubscribe();
      }

      const kmcm = this.keyMeasureCreateMethod;
      if (kmcm) {
        this._keyMeasureCreateMethodSubscription = kmcm.valueChanges
          .subscribe((value: string) => {
            if (this._createMethod !== value) {
              this._formLoadingSubject.next(true);
              this._createMethod = value;
              this.createForm();
              return;
            }
        });
      }
    }
    this._formLoadingSubject.next(false);
  }

  setValidationTimeout() {
    if (!this.isEdit) {
      // Don't run this code in create mode.
      return;
    }
    this.uniqueValidated = false;
    if (this._validationTimeout) {
      clearTimeout(this._validationTimeout);
    }
    this._validationTimeout = setTimeout(() => {
      this.validateKeyMeasureUniqueness();
    }, 500);
  }

  validateKeyMeasureUniqueness() {
    this.uniqueValidated = false;
    const localMatches = this.keyMeasures.map((keyMeasure: StrtKeyMeasurePending) => {
      return keyMeasure.measure === this.measure.value.trim();
    });

    if (localMatches.indexOf(true) >= 0) {
      // already found a local duplicate. don't bother hitting api
      this.measure.setErrors({
        'duplicateKeyMeasure': true
      });
      return;
    }

    this._keyMeasureService.validateKeyMeasureUniqueness(this.strategyId, this._pristineInitiative.initiativeId, this.measure.value)
      .subscribe(res => {
      if (!!res) {
        if (!this.measure.errors) {
          this.measure.setErrors({
            'duplicateKeyMeasure': true
          });
        } else {
          const errors = this.measure.errors;
          errors['duplicateKeyMeasure'] = true;
          this.measure.setErrors(errors);
        }
        if (this.isEdit) {
          this.measure.markAsTouched();
        }
      } else {
        this.uniqueValidated = true;
      }
    }, (err) => {
      console.error(`Failed to validate uniqueness: `, err);
    });
  }

  private clearForm() {
    if (!this.isEdit && this.hasKeyMeasures && this._createMethod === 'select') {
      this.keyMeasureForm.reset({existingKeyMeasure: '', keyMeasureCreateMethod: 'select' });
    } else if (!this.isEdit && this.hasKeyMeasures && this._createMethod === 'create') {
      this.keyMeasureForm.reset({measure: '', tag: undefined, keyMeasureCreateMethod: 'create'});
    } else {
      this.keyMeasureForm.reset({measure: '', tag: undefined});
    }
  }

  private resetForm() {
    this.clearForm();
    this.keyMeasures = [];
    this._pristineInitiative = undefined;
    this._pristineKeyMeasure = undefined;
  }

  private formToKeyMeasure(): StrtKeyMeasurePending|StrtKeyMeasurePending[] {
    if (this.isEdit) {
      const keyMeasure: StrtKeyMeasurePending = {
        measure: this.measure.value.trim(),
        tagId: this.tag.value,
        createMethod: 'edit',
        initiativeId: this._pristineInitiative.initiativeId,
        reused: false // this value doesn't really matter on edit
      };
      return Object.assign({}, this._pristineKeyMeasure, keyMeasure);
    } else {
      return this.keyMeasures;
    }
  }

  private keyMeasureToSelectItem(keyMeasure: StrtKeyMeasure): SelectItem {
    return {
      label: keyMeasure.measure,
      value: {
        keyMeasureId: keyMeasure.keyMeasureId,
        measure: keyMeasure.measure,
        tagId: keyMeasure.tagId,
        tagValue: this._rawTags.find((tag: ITag) => {
          return tag.tagId === keyMeasure.tagId;
        }).tagName}
    };
  }
}
